/**
 * A regulator's setpoint, edited where it reads on the drawing during a
 * script preview (D65): so many kPa gauge, or left empty for none, its
 * outlet relieved. Typed in kPa; what is stored is pascal (invariant 5).
 */
import { useEffect, useId, useRef, useState } from "react";
import { useKeepInside } from "./keepInside";
import { typedNumber } from "../workspace/units";

export function RegulatorSettingEditor({
  name,
  initial,
  vacuum = false,
  x,
  y,
  onCommit,
  onCancel,
}: {
  name: string;
  /** Pascal gauge, or null for none. */
  initial: number | null;
  /** Fed by a vacuum: the setpoint is how far below atmosphere it pulls. */
  vacuum?: boolean;
  /** Screen position of the status text's baseline, centred. */
  x: number;
  y: number;
  onCommit: (value: number | null) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [text, setText] = useState(initial === null || initial <= 0 ? "" : String(Number((initial / 1000).toPrecision(6))));
  const inputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  useKeepInside(formRef);
  useEffect(() => inputRef.current?.focus(), []);

  const parsed = text.trim() === "" ? null : typedNumber(text);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0);

  return (
    <form
      ref={formRef}
      data-label-editor=""
      className="cx-port-editor"
      aria-label={`Setpoint of ${name}`}
      style={{ left: x, top: y - 12 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) onCommit(parsed === null || parsed === 0 ? null : parsed * 1000);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
      onBlur={(event) => {
        // Leaving the editor altogether, not moving between its fields.
        if (!formRef.current?.contains(event.relatedTarget as Node | null)) onCancel();
      }}
    >
      <label htmlFor={`${id}-value`}>Setpoint</label>
      {vacuum && <span aria-hidden="true">−</span>}
      <input
        id={`${id}-value`}
        ref={inputRef}
        inputMode="decimal"
        value={text}
        placeholder="off"
        aria-invalid={!valid || undefined}
        onChange={(event) => setText(event.target.value.replace(/^[-−]/, ""))}
      />
      <span>kPa</span>
      {/* As in a port's editor: the press must not move focus, or WebKit blurs this form away before the click lands. */}
      <button type="submit" disabled={!valid} onMouseDown={(event) => event.preventDefault()}>
        Set
      </button>
    </form>
  );
}
